import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FaRegCircle, FaTrash, FaXmark } from 'react-icons/fa6';
import { MdDraw } from 'react-icons/md';
import { Tooltip } from 'rizzui';
import { FaExpandAlt } from 'react-icons/fa';

type DrawingMode = 'x' | 'circle';

interface CanvasEditorProps {
  imageUrl: string;
}

export interface CanvasEditorHandle {
  getImageBase64: () => string;
  clearCanvas: () => void;
}

const CanvasEditor = forwardRef<CanvasEditorHandle, CanvasEditorProps>(({ imageUrl }, ref) => {

  // STATES

  const [brushSize, setBrushSize] = useState(2); // SIZE OF BRUSH
  const [shapeSize, setShapeSize] = useState(30); // SIZE OF SHAPE
  const [mode, setMode] = useState<DrawingMode>('x');
  const [color, setColor] = useState<string>('#000000');
  const [showControls, setShowControls] = useState(false);

  // REFS

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const ctx = useRef<CanvasRenderingContext2D | null>(null);
  const startPos = useRef({ x: 0, y: 0 });
  const scale = useRef({ x: 1, y: 1 });
  const baseImage = useRef<HTMLImageElement | null>(null);

  // USE EFFECT
  
  // LOAD IMAGE AND SET CANVAS

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    ctx.current = canvas.getContext('2d');
    const img = new Image();

    img.src = imageUrl;

    img.onload = () => {

      canvas.width = 450; // WIDTH IN REAL PIXELS
      canvas.height = 400; // HEIGHT IN REAL PIXELS
      
      // CALCULATE SCALE

      const scaleX = canvas.width / img.width;
      const scaleY = canvas.height / img.height;
      scale.current = { x: scaleX, y: scaleY };

      // DRAW IMAGE SCALED

      baseImage.current = img;

      ctx.current?.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

  }, []);

  // FUNCTIONS

  // ----------------------------------------------------------------------------------------------------
  /**
   * @ES OBTENER COORDENADAS DEL CANVAS
   * @EN GET CANVAS COORDINATES
   * @param clientX
   * @param clientY
   */
  // ----------------------------------------------------------------------------------------------------
  const getCanvasCoordinates = (clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !canvasRef.current) return { x: 0, y: 0 };

    // RECALCULATE COORDINATES BASED ON SCALING

    const x = (clientX - rect.left) * (canvasRef.current.width / rect.width);
    const y = (clientY - rect.top) * (canvasRef.current.height / rect.height);

    return { x, y };
  };
  // ----------------------------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES MANEJAR EL MOUSE DOWN
   * @EN HANDLE MOUSE DOWN
   * 
   * @param e
   */
  // ---------------------------------------------------------------------------------
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    clearCanvas();
  
    isDrawing.current = true;
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    startPos.current = { x, y };
  
    if (!ctx.current) return;
    ctx.current.strokeStyle = color;
  
    if (mode === 'x') {
      drawX(x, y, color);
      isDrawing.current = false;
    }
    if (mode === 'circle') {
      drawCircle(x, y, color);
      isDrawing.current = false;
    }
  };
  // ---------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------
  /**
   * @ES MANEJAR EL MOUSE MOVE
   * @EN HANDLE MOUSE MOVE
   * 
   * @param e
   */
  // --------------------------------------------------------------------------------
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current || !ctx.current) return;
    
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    
    ctx.current.lineTo(x, y);
    ctx.current.stroke();
  };
  // --------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------
  /**
   * @ES DIBUJAR UNA X
   * @EN DRAW AN X
   * 
   * @param x
   * @param y
   * @param color
   */
  // --------------------------------------------------------------------------------
  const drawX = (x: number, y: number, color: string) => {

    if (!ctx.current) return;

    const size = shapeSize;
  
    ctx.current.strokeStyle = color;

    ctx.current.lineWidth = brushSize;

    ctx.current.strokeStyle = color;
    ctx.current.beginPath();
    ctx.current.moveTo(x - size/2, y - size/2);
    ctx.current.lineTo(x + size/2, y + size/2);
    ctx.current.moveTo(x + size/2, y - size/2);
    ctx.current.lineTo(x - size/2, y + size/2);
    ctx.current.stroke();
  };
  // --------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------
  /**
   * @ES DIBUJAR UN CÍRCULO
   * @EN DRAW A CIRCLE
   * 
   * @param x
   * @param y
   * @param color
   */
  // --------------------------------------------------------------------------------
  const drawCircle = (x: number, y: number, color: string) => {

    if (!ctx.current) return;

    const radius = shapeSize / 2;
  
    ctx.current.strokeStyle = color;
    ctx.current.lineWidth = brushSize;
    ctx.current.strokeStyle = color;
    ctx.current.beginPath();
    ctx.current.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.current.stroke();
  };
  // --------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------
  /**
   * @ES OBTENER LA IMAGEN EN BASE64
   * @EN GET IMAGE IN BASE64
   */
  // --------------------------------------------------------------------------------
  const getImageBase64 = () => {
    if (!canvasRef.current) return '';
    return canvasRef.current.toDataURL('image/png');
  };
  // --------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------
  /**
   * @ES LIMPIAR EL CANVAS
   * @EN CLEAR THE CANVAS
   */
  // --------------------------------------------------------------------------------
  const clearCanvas = () => {
    if (!ctx.current || !canvasRef.current) return;
    ctx.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    if (baseImage.current) {
      ctx.current.drawImage(baseImage.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };
  // --------------------------------------------------------------------------------

  // EXPOSED FUNCTIONS

  useImperativeHandle(ref, () => ({
    getImageBase64,
    clearCanvas,
  }));

  // RENDER

  return (
    <div className="canvas-editor">
      <div className='w-100 flex justify-center items-center'>

        {/* CONTAINER */}

        <div className='relative w-[450px] h-[420px]'>

          {/* CANVAS */}

          <canvas
            ref={canvasRef}
            className='w-full h-full bg-white border border-primary rounded'
            style={{ width: '450px', height: '420px' }} // Estilos CSS
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={() => isDrawing.current = false}
            onMouseLeave={() => isDrawing.current = false}
          />

          {/* CONTROLS */}

          <div className="flex flex-row justify-end items-center" style={{ top: '5px', right: '10px', position: 'absolute' }}>

            {/* CROSS BUTTONS */}

            <Tooltip content="Poner X" placement="bottom" color="invert">
              <button
              onClick={() => {
                setMode('x')
                setShowControls(true);
                setBrushSize(2);
                setShapeSize(30);
              }} className={mode === 'x' ? 'bg-primary p-2 text-white rounded me-2' : 'bg-[#9dbd99] p-2 text-primary rounded me-2'}>
                <FaXmark className='w-4 h-4' size={20} />
              </button>
            </Tooltip>

            {/* CIRCLE BUTTON */}

            <Tooltip content="Poner círculo" placement="bottom" color="invert">
              <button onClick={() => {
                setMode('circle')
                setShowControls(true);
                setBrushSize(2);
                setShapeSize(30);
              }} className={mode === 'circle' ? 'bg-primary p-2 text-white rounded me-2' : 'bg-[#9dbd99] p-2 text-primary rounded me-2'}>
                <FaRegCircle className='w-4 h-4' size={20} />
              </button>
            </Tooltip>

            {/* CLEAR BUTTON */}

            <Tooltip content="Borrar" placement="bottom" color="invert">
              <button onClick={() => {
                clearCanvas();
                setShowControls(false);
                setBrushSize(2);
                setShapeSize(30);
              }} 
              disabled={mode === 'x' || mode === 'circle' ? false : true}
              className='bg-primary p-2 text-white rounded me-2'>
                <FaTrash className='w-4 h-4' size={20} />
              </button>
            </Tooltip>

            {/* EXPAND BUTTON */}

            {(mode === 'circle' || mode === 'x') && (
            <Tooltip content="Colapsar opciones de formas" placement="bottom" color="invert">
              <button onClick={() => setShowControls(!showControls)} className='bg-primary p-2 text-white rounded me-2'>
                <FaExpandAlt className='w-4 h-4' size={20} />
              </button>
            </Tooltip>
            )}

            {/* COLOR PICKER */}

            <input
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              className='bg-black text-white rounded me-2 w-[30px] h-[30px]'
              title="Selecciona el color"
            />
            </div>

            {/* BRUSH SIZE AND SHAPE SIZE */}

            { showControls && ( 
              <div className="flex flex-col justify-center items-center border-primary bg-[#7bba72] border rounded p-2 text-white" 
              style={{ top: '45px', right: '10px', position: 'absolute' }}>
                <label className="text-sm text-white font-bold" htmlFor='brushSize'>Grosor del trazo</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  id='brushSize'
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  style={{
                    //@ts-ignore
                    '--progress-10': `${(brushSize / 10) * 100}%`,
                    background: `linear-gradient(to right, #36882c ${(brushSize / 10) * 100}%, #fff ${(brushSize / 10) * 100}%)`
                  }}
                  className="me-2 mt-2"
                  title="Grosor del trazo"
                />
                <label className="text-sm text-white font-bold mt-2" htmlFor='shapeSize'>Tamaño de formas</label>
                <input
                  type="range"
                  id='shapeSize'
                  min="10"
                  max="100"
                  value={shapeSize}
                  onChange={(e) => setShapeSize(Number(e.target.value))}
                  style={{
                    //@ts-ignore
                    '--progress': `${shapeSize}%`,
                    background: `linear-gradient(to right, #36882c ${shapeSize}%, #fff ${shapeSize}%)`
                  }}
                  className="me-2 mt-2"
                  title="Tamaño de la forma"
                />
              </div>
            )}
        </div>
        
      </div>

    </div>
  );
});

export default CanvasEditor;
